import * as React from 'react';
import { Box, Chip, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Markdown, { jsonMarkdown } from '../../../components/Markdown';

export interface ToolActivityEntry {
  id: string;
  name: string;
  args: unknown;
  result?: unknown;
  rejected?: boolean;
  failed?: boolean;
}

function summaryLabel(entries: ToolActivityEntry[]): string {
  const names = new Set(entries.map((entry) => entry.name));
  if (names.size === 1) {
    const name = entries[0].name;
    return entries.length === 1 ? name : `${entries.length} × ${name}`;
  }
  return `${entries.length} tool calls`;
}

function StatusIcon({ entry }: { entry: ToolActivityEntry }) {
  if (entry.rejected) {
    return <BlockOutlinedIcon fontSize="small" color="warning" />;
  }
  if (entry.failed) {
    return <ErrorOutlineIcon fontSize="small" color="error" />;
  }
  if (entry.result !== undefined) {
    return <CheckCircleOutlineIcon fontSize="small" color="success" />;
  }
  return null;
}

function ToolCallRow({ entry }: { entry: ToolActivityEntry }) {
  const [open, setOpen] = React.useState(false);
  return (
    <Box>
      <Box
        onClick={() => setOpen((value) => !value)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          px: 1,
          py: 0.75,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {open ? (
          <ExpandLessIcon fontSize="small" color="action" />
        ) : (
          <ExpandMoreIcon fontSize="small" color="action" />
        )}
        <Typography variant="body2" sx={{ fontFamily: 'monospace', flex: 1, overflowWrap: 'anywhere' }}>
          {entry.name}
        </Typography>
        <StatusIcon entry={entry} />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 4, pr: 1, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Arguments
          </Typography>
          <Markdown>{jsonMarkdown(entry.args)}</Markdown>
          {entry.rejected ? (
            <Typography variant="caption" color="warning.main">
              You rejected this action.
            </Typography>
          ) : (
            entry.result !== undefined && (
              <>
                <Typography variant="caption" color={entry.failed ? 'error.main' : 'text.secondary'}>
                  {entry.failed ? 'Failed' : 'Result'}
                </Typography>
                <Markdown>{jsonMarkdown(entry.result)}</Markdown>
              </>
            )
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export function ToolActivityGroup({ entries }: { entries: ToolActivityEntry[] }) {
  const [open, setOpen] = React.useState(false);
  const rejectedCount = entries.filter((entry) => entry.rejected).length;
  const failedCount = entries.filter((entry) => entry.failed).length;

  return (
    <Box sx={{ mb: 2, maxWidth: '80%' }}>
      <Box
        onClick={() => setOpen((value) => !value)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <BuildOutlinedIcon fontSize="small" />
        <Typography variant="body2">{summaryLabel(entries)}</Typography>
        {rejectedCount > 0 && (
          <Chip size="small" color="warning" variant="outlined" label={`${rejectedCount} rejected`} />
        )}
        {failedCount > 0 && <Chip size="small" color="error" variant="outlined" label={`${failedCount} failed`} />}
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            mt: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            '& > * + *': { borderTop: '1px solid', borderColor: 'divider' },
          }}
        >
          {entries.map((entry, index) => (
            <ToolCallRow key={entry.id || index} entry={entry} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
